import { Category } from '../../shared/issuesV2';
import { Action, ResourceType } from '../../typings/permissions';
import { window } from '../globals';

export const NO_FILTER_OPTION_LABEL = 'All issues (not filtered)';

/**
 * Saved filters permissions.
 */

//  Map to lookup the correct resource type based on category.
const savedFiltersResourceTypeMap: Record<Category, ResourceType> = {
  [Category.Licensing]: ResourceType.IssueFilterLicensing,
  [Category.Vulnerability]: ResourceType.IssueFilterSecurity,
  [Category.Quality]: ResourceType.IssueFilterQuality,
};

/**
 * Returns a function for checking a saved filter permission based on an action.
 *
 * @param {Action.Create | Action.Edit | Action.Delete} action - Permission action.
 * @param {Category} category - Issue category
 */
const hasSavedFiltersPermission = (
  action: Action.Create | Action.Edit | Action.Delete,
  category: Category,
): boolean => {
  const resourceType = savedFiltersResourceTypeMap?.[category] ?? null;
  //  This should never happen, but makes us type safe.
  if (!resourceType) {
    return false;
  }
  return window.hasPermission({ action, resourceType });
};

export type SavedFiltersPermission = {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  //  Helper for checking if the entire actions dropdown should be disabled.
  canAny: boolean;
};

/**
 * Checks if the user has the ability to Create, Edit and/or Delete
 * saved filters based on the current category.
 *
 * @param {Category} category - Issue category
 */
export const getSavedFiltersPermissions = (category: Category): SavedFiltersPermission => {
  //  Note that there is no Create or Delete permissions action for projects,
  //  so those are just true so long as canEdit is true.
  const canEdit = hasSavedFiltersPermission(Action.Edit, category);
  const canCreate = hasSavedFiltersPermission(Action.Create, category);
  const canDelete = hasSavedFiltersPermission(Action.Delete, category);
  return {
    canCreate,
    canEdit,
    canDelete,
    canAny: canCreate || canEdit || canDelete,
  };
};
